/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
/* eslint-disable @typescript-eslint/naming-convention */
import Joi from '@hapi/joi';
import React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import type { IZxyRegistration } from '../../../../common/src/domain/zxyExchange/IZxyRegistration';
import { ObjectStates as ObjectStatus } from '../../../../common/src/enums/ObjectStates';
import { Roles } from '../../../../common/src/enums/Roles';
import { withReactRouter } from '../../mixins/router/withReactRouter';
import { ITranslateProps, translate } from '../../mixins/translate';
import { ILanguageCodeProps, IValidationComponentState, ValidatingComponent } from '../../mixins/validation/validation';
import { fetchGet, fetchPost } from '../../store/fetch';
import { openApiFetchGet } from '../../store/openApiFetch';
import { IConnectProps } from '../../utils/IConnectProps';
import { IRouterProps } from '../../utils/IRouterProps';
import { EditPanelMarkdown } from '../common/markdown/EditPanelMarkdown';
import { Modal } from '../common/Modal/Modal';
import {
    Content,
    EditPanelButton,
    EditPanelButtons,
    EditPanelCell,
    EditPanelExplanation,
    EditPanelFilterCreateSelect,
    EditPanelInput,
    EditPanelRow,
    EditPanelSection,
    EditPanelSelect,
    Panel,
    PanelBody,
    PanelButtonLink,
    PanelHeader,
    TopbarCrumb,
    TopbarCrumbActive,
    TopbarLeftOnly,
    EditPanelFilterSelect,
    Tray,
} from '../elements';
import { GenericMap } from '../utils/GenericMap';
import { ObjectDrawShapeModal } from './ObjectDrawShapeModal';

interface IObjectAddEditProps extends ITranslateProps, IConnectProps, ILanguageCodeProps, IRouterProps {
    userRole: string;
    languageCode: string;
    useBatches: boolean;
    isClient: boolean;
    showHeader?: boolean;
    isPdokMapLayerAvailable: boolean;
    isConfigurationManager: boolean;
}

interface IValueText {
    value: string;
    text: string;
}

// eslint-disable-next-line no-shadow
enum ZxyExchangeAccess {
    NoAccess = 'NoAccess',
    Anyone = 'Anyone',
    Selected = 'Selected',
}

interface IMarker {
    key: string;
    position: {
        lat: number;
        lng: number;
    };
    title: string;
    color?: undefined | string;
}

export interface IObjectAddEditState extends IValidationComponentState {
    clientCompanies: IValueText[];
    clientCompanyId?: undefined | string; // the one who pay the bill
    code?: undefined | string;
    externalRefId?: undefined | string;
    inspectionStandardId?: undefined | string;
    inspectionStandards: IValueText[];
    isPublic: boolean;
    latitude?: undefined | number;
    location?: undefined | string;
    longitude?: undefined | number;
    name?: undefined | string;
    objectTypeId?: undefined | string;
    objectTypes: IValueText[];
    operatorCompanies: IValueText[];
    operatorCompanyId?: undefined | string;
    ownerCompanies: IValueText[];
    ownerCompanyId?: undefined | string;
    status?: undefined | string;
    surveyorCompanies: IValueText[];
    surveyorCompanyId?: undefined | string;
    zxyRegisteredServers: IValueText[];
    zxyRegistration?: undefined | IZxyRegistration;
    zxyExchangeAccessType?: undefined | ZxyExchangeAccess;
    zxyExchangeAccessRegistrationId?: undefined | string;
    isLatLngMapDialogVisible: boolean;
    isShapeMapDialogVisible: boolean;
    remarks?: undefined | string;

    selectedLatitude?: undefined | number;
    selectedLongitude?: undefined | number;
    clientLatitude: number;
    clientLongitude: number;
    markers?: undefined | IMarker[];
    shapeGeoJson?: undefined | Record<string, unknown>;
    labels: string[];
    knownLabels: string[];
}

function mapStateToProps(state: any) {
    const { authentication, language } = state;
    return {
        userRole: authentication.role,
        languageCode: language.code,
        useBatches: authentication.useBatches,
        isClient: authentication.isClient,
        isConfigurationManager: authentication.isConfigurationManager,
        isPdokMapLayerAvailable: authentication.isPdokMapLayerAvailable,
    };
}

class ObjectAddEdit extends ValidatingComponent<IObjectAddEditProps, IObjectAddEditState> {
    constructor(props: IObjectAddEditProps) {
        super(props);

        this.state = {
            clientCompanies: [],
            clientCompanyId: this.props.location.query.clientCompanyId,
            errors: {},
            failed: false,
            inspectionStandards: [],
            isPublic: false,
            objectTypes: [],
            operatorCompanies: [],
            ownerCompanies: [],
            saved: false,
            status: ObjectStatus.InUse,
            submitting: false,
            surveyorCompanies: [],
            zxyRegisteredServers: [],
            isLatLngMapDialogVisible: false,
            isShapeMapDialogVisible: false,
            clientLatitude: 52.15518,
            clientLongitude: 5.3872,
            labels: [],
            knownLabels: [],
        };
    }

    public override async componentDidMount() {
        if (this.props.match.params.id) {
            // edit object
            await this.load();
        } else {
            // new object
            await this.loadObjectTypes();
            if (this.props.userRole === Roles.Administrator) {
                await this.loadClientCompanies();
            } else {
                await this.loadCompanies();
            }
        }
        void this.loadClientCoordinates();
        void this.loadRegistration();
        void this.loadKnownLabels();
    }

    public getValidatorTypes(): any {
        return {
            name: Joi.string().required().label(this.props.t('common.name')),
            code: Joi.string().required().label(this.props.t('common.code')),
            location: Joi.string().required().label(this.props.t('object.locationDescription')),
            objectTypeId: Joi.string().required().label(this.props.t('common.objectType')),
            latitude: Joi.number().label(this.props.t('object.latitude')).allow(null, ''),
            longitude: Joi.number().label(this.props.t('object.longitude')).allow(null, ''),
        };
    }

    public getValidatorData() {
        let { inspectionStandardId } = this.state;
        if (this.state.inspectionStandards.length === 1) {
            inspectionStandardId = this.state.inspectionStandards[0]!.value;
        }
        return {
            clientCompanyId: this.state.clientCompanyId,
            code: this.state.code,
            externalRefId: this.state.externalRefId,
            inspectionStandardId,
            isPublic: this.state.isPublic,
            latitude: this.state.latitude,
            location: this.state.location,
            longitude: this.state.longitude,
            name: this.state.name,
            objectTypeId: this.state.objectTypeId,
            operatorCompanyId: this.state.operatorCompanyId,
            ownerCompanyId: this.state.ownerCompanyId,
            status: this.state.status,
            surveyorCompanyId: this.state.surveyorCompanyId,
            zxyExchangeAccessType: this.state.zxyExchangeAccessType,
            zxyExchangeAccessRegistrationId: this.state.zxyExchangeAccessRegistrationId,
            shapeGeoJson: this.state.shapeGeoJson ?? null,
            labels: this.state.labels,
            remarks: this.state.remarks,
        };
    }

    public override async componentDidUpdate(prevProps: IObjectAddEditProps) {
        if (this.props.languageCode !== prevProps.languageCode) {
            await this.loadObjectTypes();
        }
    }

    public override render() {
        const canSelectClient = this.props.userRole === Roles.Administrator && this.state.clientCompanies.length > 1;
        const canSelectOwner = this.state.ownerCompanies.length > 0 && this.props.userRole !== Roles.Owner;
        const canEdit = this.props.isConfigurationManager || !this.props.match.params.id;
        const zxyRegistrationReadObject =
            this.state.zxyRegistration?.isAllowedAddSurvey ||
            this.state.zxyRegistration?.isAllowedReadObjectData ||
            this.state.zxyRegistration?.isAllowedReadObjectList;

        const hasShape = this.state.shapeGeoJson !== null && this.state.shapeGeoJson !== undefined;
        const hasPoint = (!hasShape && (this.state.latitude ?? false)) as boolean;

        return (
            <div>
                {this.state.isLatLngMapDialogVisible && this.MapDialog()}
                {this.state.isShapeMapDialogVisible && (
                    <ObjectDrawShapeModal
                        zoom={this.state.latitude && this.state.longitude ? 17 : 10}
                        center={{ lat: this.state.latitude ?? this.state.clientLatitude, lng: this.state.longitude ?? this.state.clientLongitude }}
                        initialShapeGeoJson={this.state.shapeGeoJson}
                        isPdokMapLayerAvailable={this.props.isPdokMapLayerAvailable}
                        onSave={(shapeGeoJson) => this.setState({ shapeGeoJson, isShapeMapDialogVisible: false })}
                        onClose={() => this.setState({ isShapeMapDialogVisible: false })}
                    />
                )}
                {this.props.showHeader && (
                    <TopbarLeftOnly>
                        <TopbarCrumbActive icon="fa-building" title={this.props.t('common.addItem', this.props.t('common.object'))} />
                        <TopbarCrumb>
                            <Link to="/objects">{this.props.t('common.objects')}</Link>
                        </TopbarCrumb>
                    </TopbarLeftOnly>
                )}

                <Content>
                    <Tray>
                        {this.renderMessage()}
                        <Panel>
                            <form method="post" action="/" id="admin-form" onSubmit={() => this.saveClicked()}>
                                <PanelBody>
                                    {/* Name */}
                                    <EditPanelRow>
                                        <EditPanelCell rowCount={9}>
                                            <EditPanelInput
                                                name="name"
                                                placeholder={this.props.t('common.name')}
                                                topLabel={this.props.t('common.name')}
                                                value={this.state.name}
                                                icon="fa fa-building"
                                                handleChange={this.handleChange.bind(this)}
                                                notValid={!this.isValid('name')}
                                            />
                                            {this.renderHelpText(this.getValidationMessages('name'))}
                                        </EditPanelCell>
                                        <EditPanelCell rowCount={3}>
                                            <EditPanelInput
                                                name="code"
                                                placeholder={this.props.t('common.code')}
                                                topLabel={this.props.t('common.code')}
                                                value={this.state.code}
                                                icon="fa fa-circle-o"
                                                handleChange={this.handleChange.bind(this)}
                                                notValid={!this.isValid('code')}
                                            />
                                            {this.renderHelpText(this.getValidationMessages('code'))}
                                        </EditPanelCell>
                                    </EditPanelRow>

                                    {/* Type / Inspection standard */}
                                    <EditPanelRow>
                                        <EditPanelCell rowCount={4}>
                                            <EditPanelSelect
                                                name="objectTypeId"
                                                value={this.state.objectTypeId}
                                                handleChange={this.handleChange.bind(this)}
                                                notValid={!this.isValid('objectTypeId')}
                                                topLabel={this.props.t('common.objectType')}
                                                topLabelTooltip={this.props.t('object.typeTooltip')}
                                                disabled={this.state.submitting || !canEdit}
                                                placeholder={this.props.t('common.selectItem', this.props.t('common.objectType'))}
                                                options={this.state.objectTypes}
                                            />
                                            {this.renderHelpText(this.getValidationMessages('objectTypeId'))}
                                        </EditPanelCell>
                                        {this.state.inspectionStandards.length !== 0 && (
                                            <EditPanelCell rowCount={4}>
                                                <EditPanelSelect
                                                    name="inspectionStandardId"
                                                    value={this.state.inspectionStandardId}
                                                    handleChange={this.handleChange.bind(this)}
                                                    topLabel={this.props.t('object.objectBreakdown')}
                                                    disabled={this.state.submitting || !canEdit}
                                                    options={this.state.inspectionStandards}
                                                />
                                            </EditPanelCell>
                                        )}

                                        <EditPanelCell rowCount={4}>
                                            <EditPanelSelect
                                                name="status"
                                                value={this.state.status}
                                                topLabel={this.props.t('common.status')}
                                                handleChange={this.handleChange.bind(this)}
                                                options={this.objectStates()}
                                            />
                                        </EditPanelCell>
                                    </EditPanelRow>

                                    <EditPanelSection>
                                        <EditPanelInput
                                            name="location"
                                            topLabel={this.props.t('common.location')}
                                            placeholder={this.props.t('object.locationDescription')}
                                            value={this.state.location}
                                            icon="fa fa-map-marker"
                                            handleChange={this.handleChange.bind(this)}
                                            notValid={!this.isValid('location')}
                                        />
                                        {this.renderHelpText(this.getValidationMessages('location'))}
                                    </EditPanelSection>
                                    <EditPanelSection>
                                        <EditPanelMarkdown
                                            value={this.state.remarks ?? ''}
                                            name="remarks"
                                            handleChange={this.handleChange.bind(this)}
                                            placeholder={this.props.t('common.remarks')}
                                            topLabel={this.props.t('common.remarks')}
                                            hasTableEditor={false}
                                        />
                                    </EditPanelSection>

                                    {/* location */}
                                    <PanelHeader text={this.props.t('object.position')} />

                                    <EditPanelSection>
                                        {hasShape && <span>{this.props.t('object.hasShape')} </span>}
                                        {hasPoint && <span>{this.props.t('object.hasPoint')} </span>}
                                        {!hasShape && !hasPoint && <span>{this.props.t('object.givePosition')}</span>}
                                    </EditPanelSection>
                                    <EditPanelRow>
                                        <EditPanelCell rowCount={4}>
                                            <PanelButtonLink
                                                id="drawShapeButton"
                                                text={this.props.t('object.drawShape')}
                                                onClick={() => void this.setState({ isShapeMapDialogVisible: true })}
                                                small
                                                icon="fa fa-globe"
                                            ></PanelButtonLink>
                                        </EditPanelCell>
                                    </EditPanelRow>
                                    {!hasShape && (
                                        <EditPanelRow>
                                            <EditPanelCell rowCount={4}>
                                                <div className="mt20">
                                                    <PanelButtonLink
                                                        text={this.props.t('object.selectPoint')}
                                                        onClick={() => void this.setState({ isLatLngMapDialogVisible: true })}
                                                        small
                                                        icon="fa fa-globe"
                                                    ></PanelButtonLink>
                                                </div>
                                            </EditPanelCell>
                                            <EditPanelCell rowCount={4}>
                                                <EditPanelInput
                                                    name="latitude"
                                                    topLabel="Latitude"
                                                    placeholder={this.props.t('object.latitude')}
                                                    value={this.state.latitude ? this.state.latitude.toString() : ''}
                                                    icon="fa fa-circle-o"
                                                    handleChange={this.handleChange.bind(this)}
                                                    notValid={!this.isValid('latitude')}
                                                />
                                                {this.renderHelpText(this.getValidationMessages('latitude'))}
                                            </EditPanelCell>
                                            <EditPanelCell rowCount={4}>
                                                <EditPanelInput
                                                    name="longitude"
                                                    topLabel="Longitude"
                                                    placeholder={this.props.t('object.longitude')}
                                                    value={this.state.longitude ? this.state.longitude.toString() : ''}
                                                    icon="fa fa-circle-o"
                                                    handleChange={this.handleChange.bind(this)}
                                                    notValid={!this.isValid('longitude')}
                                                />
                                                {this.renderHelpText(this.getValidationMessages('longitude'))}
                                            </EditPanelCell>
                                        </EditPanelRow>
                                    )}

                                    <PanelHeader text={this.props.t('common.labels')} />
                                    <EditPanelRow>
                                        <EditPanelCell rowCount={12}>
                                            <EditPanelFilterCreateSelect
                                                name="labels"
                                                value={this.state.labels}
                                                placeholder={this.props.t('common.selectItem', this.props.t('common.labels'))}
                                                options={this.state.knownLabels}
                                                handleChange={this.handleChange.bind(this)}
                                            />

                                            <div className="mt15">
                                                <EditPanelExplanation text={this.props.t('survey.labelsExpl')} />
                                            </div>
                                        </EditPanelCell>
                                    </EditPanelRow>

                                    {(!this.props.useBatches || canSelectClient || canSelectOwner) && (
                                        <PanelHeader text={this.props.t('object.companyRelations')} />
                                    )}

                                    {/* Only a administrator can select a client */}
                                    {canSelectClient && (
                                        <EditPanelRow>
                                            <EditPanelCell rowCount={6}>
                                                <EditPanelFilterSelect
                                                    name="clientCompanyId"
                                                    value={this.state.clientCompanyId}
                                                    handleChange={this.handleChange.bind(this)}
                                                    topLabel={this.props.t('common.client')}
                                                    placeholder={this.props.t('common.selectItem', this.props.t('common.client'))}
                                                    options={this.state.clientCompanies}
                                                />
                                            </EditPanelCell>
                                        </EditPanelRow>
                                    )}

                                    {/* Select owner.*/}
                                    {canSelectOwner && (
                                        <EditPanelRow>
                                            <EditPanelCell rowCount={6}>
                                                <EditPanelFilterSelect
                                                    name="ownerCompanyId"
                                                    value={this.state.ownerCompanyId}
                                                    handleChange={this.handleChange.bind(this)}
                                                    topLabel={this.props.t('common.owner')}
                                                    placeholder={this.props.t('common.selectItem', this.props.t('common.owner'))}
                                                    options={this.state.ownerCompanies}
                                                />
                                            </EditPanelCell>
                                        </EditPanelRow>
                                    )}

                                    <EditPanelRow>
                                        {/* Select surveyor */}
                                        {this.state.surveyorCompanies.length > 0 &&
                                            this.props.userRole !== Roles.Surveyor &&
                                            !this.props.useBatches && (
                                                <EditPanelCell rowCount={6}>
                                                    <EditPanelFilterSelect
                                                        name="surveyorCompanyId"
                                                        value={this.state.surveyorCompanyId}
                                                        handleChange={this.handleChange.bind(this)}
                                                        topLabel={this.props.t('common.surveyor')}
                                                        placeholder={this.props.t('common.selectItem', this.props.t('common.surveyor'))}
                                                        options={this.state.surveyorCompanies}
                                                    />
                                                </EditPanelCell>
                                            )}

                                        {/* Select operator */}
                                        {this.state.operatorCompanies.length > 0 &&
                                            this.props.userRole !== Roles.Operator &&
                                            !this.props.useBatches && (
                                                <EditPanelCell rowCount={6}>
                                                    <EditPanelFilterSelect
                                                        name="operatorCompanyId"
                                                        value={this.state.operatorCompanyId}
                                                        handleChange={this.handleChange.bind(this)}
                                                        topLabel={this.props.t('common.operator')}
                                                        placeholder={this.props.t('common.selectItem', this.props.t('common.operator'))}
                                                        options={this.state.operatorCompanies}
                                                    />
                                                </EditPanelCell>
                                            )}
                                    </EditPanelRow>

                                    {(this.props.userRole === Roles.Administrator || this.props.isClient) && zxyRegistrationReadObject && (
                                        <>
                                            <PanelHeader text={this.props.t('object.zxyExchangeAccess')} />

                                            <EditPanelRow>
                                                <EditPanelCell rowCount={6}>
                                                    <EditPanelSelect
                                                        name="zxyExchangeAccessType"
                                                        value={this.state.zxyExchangeAccessType}
                                                        handleChange={this.handleChange.bind(this)}
                                                        topLabel={this.props.t('object.accessLevel')}
                                                        options={[
                                                            { value: ZxyExchangeAccess.Anyone, text: this.props.t('object.anyoneAccess') },
                                                            { value: ZxyExchangeAccess.NoAccess, text: this.props.t('object.noAccess') },
                                                            { value: ZxyExchangeAccess.Selected, text: this.props.t('object.selectedAccess') },
                                                        ]}
                                                    />
                                                </EditPanelCell>
                                                {this.state.zxyExchangeAccessType === ZxyExchangeAccess.Selected && (
                                                    <EditPanelCell rowCount={6}>
                                                        <EditPanelSelect
                                                            name="zxyExchangeAccessRegistrationId"
                                                            value={this.state.zxyExchangeAccessRegistrationId}
                                                            handleChange={this.handleChange.bind(this)}
                                                            topLabel={this.props.t('object.registeredCompanies')}
                                                            disabled={this.state.submitting}
                                                            placeholder={this.props.t(
                                                                'common.selectItem',
                                                                this.props.t('object.registeredCompanies'),
                                                            )}
                                                            options={this.state.zxyRegisteredServers}
                                                        />
                                                    </EditPanelCell>
                                                )}
                                            </EditPanelRow>
                                        </>
                                    )}

                                    {/* <EditPanelSection>
                                                <EditPanelSwitch name="isPublic" placeholder={this.props.t('object.isPublic')} value={this.state.isPublic} handleChange={this.handleChange.bind(this)} />
                                            </EditPanelSection> */}
                                </PanelBody>

                                <EditPanelButtons footer>
                                    <EditPanelButton
                                        onClick={() => this.props.navigate('/objects')}
                                        text={this.props.t('common.cancel')}
                                        disabled={this.state.submitting}
                                    />
                                    <EditPanelButton
                                        onClick={() => this.saveClicked()}
                                        text={this.props.t('common.save')}
                                        primary={true}
                                        disabled={this.state.submitting}
                                    />
                                </EditPanelButtons>
                            </form>
                        </Panel>
                    </Tray>
                </Content>
            </div>
        );
    }

    protected override async onChanged(value: any, stateKey: string) {
        if (stateKey === 'objectTypeId') {
            if (value && value !== '') {
                await this.loadValueText(`/api/inspectionStandards?objectTypeId=${value}`, 'inspectionStandards');
                await this.setStatePromised({ inspectionStandardId: this.state.inspectionStandards[0]!.value });
            } else {
                await this.setStatePromised({ inspectionStandards: [], inspectionStandardId: undefined });
            }
        }

        // if client is selected load companies
        if (stateKey === 'clientCompanyId') {
            if (value && value !== '') {
                await this.loadCompanies(value);
            } else {
                await this.setStatePromised({
                    ownerCompanies: [],
                    surveyorCompanies: [],
                    operatorCompanies: [],
                    ownerCompanyId: undefined,
                    surveyorCompanyId: undefined,
                    operatorCompanyId: undefined,
                });
            }
        }
    }

    private async loadValueText(url: string, stateKey: string) {
        const result = await fetchGet(url, undefined, ['NO_DATA_AVAILABLE'], [404, 403]);
        const items = result.json?.items ? result.json.items : result.json;
        if (items && items.length > 0) {
            const valueTextArray = items.map((item: any) => ({ value: item.id, text: item.name }));
            const newState = {} as any;
            newState[stateKey] = valueTextArray;
            await this.setStatePromised(newState);
        }
    }

    private async loadObjectTypes() {
        const url = `/api/objectTypesList?languageCode=${this.props.languageCode}`;
        await this.loadValueText(url, 'objectTypes');
    }

    private async loadCompanies(clientCompanyId?: string) {
        if (clientCompanyId) {
            await this.loadValueText(`/api/companyList?type=owner&companyId=${clientCompanyId}`, 'ownerCompanies');
            await this.loadValueText(`/api/companyList?type=surveyor&companyId=${clientCompanyId}`, 'surveyorCompanies');
            await this.loadValueText(`/api/companyList?type=operator&companyId=${clientCompanyId}`, 'operatorCompanies');
        } else {
            await this.loadValueText('/api/companyList?type=owner', 'ownerCompanies');
            await this.loadValueText('/api/companyList?type=surveyor', 'surveyorCompanies');
            await this.loadValueText('/api/companyList?type=operator', 'operatorCompanies');
        }
    }

    private async loadClientCompanies() {
        await this.loadValueText('/api/clients', 'clientCompanies');

        if (this.props.location.query.clientCompanyId) {
            await this.handleChange(this.props.location.query.clientCompanyId, 'clientCompanyId');
        }
        if (this.state.clientCompanies.length === 1) {
            await this.handleChange(this.state.clientCompanies[0]!.value, 'clientCompanyId');
        }
    }

    private async load() {
        const url = `/api/objects/${this.props.match.params.id}`;
        const result = await fetchGet(url);

        await this.setStatePromised(result.json);
        await this.loadObjectTypes();

        if (this.state.objectTypeId) {
            await this.loadValueText(`/api/inspectionStandards?objectTypeId=${this.state.objectTypeId}`, 'inspectionStandards');
        }

        if (this.props.userRole === Roles.Administrator) {
            await this.loadClientCompanies();
            await this.loadCompanies(result.json.clientCompanyId);
        } else {
            await this.loadCompanies();
        }
        if (this.state.latitude && this.state.longitude) {
            await this.addMarker({ latitude: this.state.latitude, longitude: this.state.longitude });
        }
    }

    private async loadRegistration() {
        try {
            const result = await openApiFetchGet('/api/registrationsClient', {});
            await this.setStatePromised({ zxyRegistration: result });
            const registeredServers = await openApiFetchGet('/api/registrations', {});
            if (registeredServers && registeredServers.length > 0) {
                const valueTextArray = registeredServers
                    .filter((item: IZxyRegistration) => item.id !== result.id)
                    .map((item: IZxyRegistration) => ({ value: item.id, text: item.zxyClientCompanyName }));
                await this.setStatePromised({ zxyRegisteredServers: valueTextArray });
            }
        } catch (error) {
            console.log(error);
        }
    }

    private objectStates() {
        const options = [
            { value: ObjectStatus.UnderConstruction, text: this.props.t('objectStatus.underConstruction') },
            { value: ObjectStatus.InUse, text: this.props.t('objectStatus.inUse') },
            { value: ObjectStatus.InMaintenance, text: this.props.t('objectStatus.inMaintenance') },
            { value: ObjectStatus.OutOfService, text: this.props.t('objectStatus.outOfService') },
            { value: ObjectStatus.Cancelled, text: this.props.t('objectStatus.cancelled') },
        ];

        // administrator can edit object to status deleted
        if ((this.props.userRole === Roles.Administrator || this.props.isClient) && this.props.match.params.id) {
            options.push({ value: ObjectStatus.Deleted, text: this.props.t('objectStatus.deleted') });
        }

        return options;
    }

    private saveClicked() {
        this.validate(async (validationError: any) => {
            if (!validationError) {
                await this.setStatePromised({ message: undefined, submitting: true, errorCode: undefined, failed: false });
                const data = this.getValidatorData() as any;
                let url = '/api/objects';
                if (this.props.match.params.id) {
                    url += `/${this.props.match.params.id}`;
                }
                const result = await fetchPost(url, {}, data, ['NO OWNER', 'NO CLIENT', 'NO INSPECTIONSTANDARD', 'CODE IN USE']);
                if (result.errorCode && result.errorCode !== '') {
                    await this.setStatePromised({ submitting: false, errorCode: result.errorCode, failed: true });
                } else {
                    await this.setStatePromised({ submitting: false, failed: false });
                    if (this.props.match.params.id) {
                        this.props.navigate(`/objects/${this.props.match.params.id}`);
                    } else {
                        this.props.navigate(`/objects/${result.json.id}`);
                    }
                }
            }
        });
    }

    private async addMarker(position: { latitude: number; longitude: number }) {
        const markers = [] as IMarker[];
        markers.push({
            key: '1',
            position: { lat: position.latitude, lng: position.longitude },
            title: this.props.t('common.selectedPoint'),
            color: '#6194d0',
        });
        await this.setStatePromised({ markers });
    }

    private async loadClientCoordinates() {
        const url = '/api/clientCoordinates';
        const result = await fetchGet(url);
        await this.setStatePromised({ clientLatitude: result.json.latitude, clientLongitude: result.json.longitude });
    }

    private async loadKnownLabels() {
        const url = '/api/objects/knownLabels';
        const result = await fetchGet(url);
        await this.setStatePromised({ knownLabels: result?.json });
    }

    private async mapClicked(longitude: number, latitude: number) {
        await this.setStatePromised({ selectedLatitude: latitude, selectedLongitude: longitude });
    }

    private async saveSelectedMapPosition() {
        await this.setStatePromised({
            latitude: this.state.selectedLatitude,
            longitude: this.state.selectedLongitude,
            isLatLngMapDialogVisible: false,
        });
    }

    private MapDialog() {
        const markers: IMarker[] = [];
        const markerLatitude = this.state.selectedLatitude ?? this.state.latitude;
        const markerLongitude = this.state.selectedLongitude ?? this.state.longitude;

        if (markerLatitude && markerLongitude) {
            markers.push({
                key: `${markerLatitude}_${markerLongitude}`,
                position: {
                    lat: markerLatitude,
                    lng: markerLongitude,
                },
                title: this.props.t('common.selectedPoint'),
                color: '#6194d0',
            });
        }
        return (
            <Modal
                id="map-dialog"
                classNames="confirm-dialog"
                closeOnBackdropClick={true}
                onClose={() => void this.setState({ isLatLngMapDialogVisible: false })}
            >
                <Modal.Content isScrollBarVisible={true} style={{ height: '650px', width: '800px' }}>
                    <GenericMap
                        mapClicked={this.mapClicked.bind(this)}
                        markers={markers}
                        zoom={this.state.latitude && this.state.longitude ? 17 : 10}
                        isDroneNoFlyMapNlAvailable={false}
                        isPdokMapLayerAvailable={this.props.isPdokMapLayerAvailable}
                        center={{ lat: this.state.latitude ?? this.state.clientLatitude, lng: this.state.longitude ?? this.state.clientLongitude }}
                    />
                </Modal.Content>
                <Modal.Status>
                    {this.props.t('common.selectedPoint')}: {this.state.selectedLatitude} {this.state.selectedLongitude}
                </Modal.Status>
                <Modal.Actions>
                    <EditPanelButton
                        small={true}
                        text={this.props.t('common.cancel')}
                        onClick={() => void this.setState({ isLatLngMapDialogVisible: false })}
                    />
                    <EditPanelButton
                        small={true}
                        text={this.props.t('common.save')}
                        primary
                        onClick={async () => await this.saveSelectedMapPosition()}
                    />
                </Modal.Actions>
            </Modal>
        );
    }
}

// eslint-disable-next-line import/no-default-export
export default translate()(connect(mapStateToProps, undefined, undefined, { forwardRef: true })(withReactRouter(ObjectAddEdit)));
